/**
 * Needs your attention → Home visit follow-up (1.5.12): a home visit that ended "Needs a follow-up" or "Not suitable
 * now". Administrator and co-administrators only (the server sends it to nobody else). It stays until a later home
 * visit is recorded for the person, or someone presses Mark handled and writes a note — the note, who and when are
 * kept; nothing is deleted. The line opens the person's card.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { AttentionItem } from '@shared/api-types';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { formatDayShort } from '@/lib/dates';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';

export function HomeVisitFollowUpItem({ item, dot }: { item: AttentionItem; dot: string }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const who = [item.tenantName, item.unit].filter(Boolean).join(' · ');
  const id = `hvfu-${item.id}`;
  const outcome = item.action === 'not-suitable' ? t('hv.outcomeOpt.not-suitable') : t('hv.outcomeOpt.follow-up');

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/people/home-visit-follow-ups/${encodeURIComponent(item.id)}/handled`, { method: 'POST', body: JSON.stringify({ note: note.trim() }) });
      announce(t('attention.hvHandledDone', { who }));
      setOpen(false);
      setNote('');
      void qc.invalidateQueries({ queryKey: ['attention'] });
    } catch (e) {
      const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : String(e);
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`attn-hv-${item.id}`}>
      <span className="dot" style={{ background: dot }} aria-hidden="true" />
      <div className="t">
        <b>
          {t('attention.hvFollowUp')} · {who}
        </b>
        <span>
          {outcome}
          {item.text ? ` — ${item.text}` : ''}
          {item.dayKey ? ` · ${t('attention.hvVisitOn', { day: formatDayShort(item.dayKey, i18n.language) })}` : ''}
        </span>
        {open ? (
          <div className="field" style={{ marginTop: 8 }}>
            <label htmlFor={id}>{t('attention.hvNoteLabel')}</label>
            <textarea id={id} className="input" value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} aria-required="true" aria-describedby={`${id}-help`} autoFocus data-testid={`hv-handled-note-${item.id}`} />
            <span className="help" id={`${id}-help`}>
              {t('attention.hvNoteHelp')}
            </span>
            <div className="row" style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="sm" disabled={busy || !note.trim()} onClick={() => send()} data-testid={`hv-handled-send-${item.id}`}>
                {t('attention.hvMarkHandled')}
              </Button>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>
                {t('book.cancel')}
              </Button>
            </div>
          </div>
        ) : null}
        {error ? (
          <span className="field-err" role="alert">
            {error}
          </span>
        ) : null}
      </div>
      {item.tenantId ? (
        <Link className="btn secondary sm" to={`/people/${encodeURIComponent(item.tenantId)}`} aria-label={`${t('attention.hvOpenPerson')} · ${who}`} data-testid={`hv-open-${item.id}`}>
          {t('attention.hvOpenPerson')}
        </Link>
      ) : null}
      {!open ? (
        <Button size="sm" variant="secondary" aria-label={`${t('attention.hvMarkHandled')} · ${who}`} onClick={() => setOpen(true)} data-testid={`hv-handled-${item.id}`}>
          {t('attention.hvMarkHandled')}
        </Button>
      ) : null}
    </li>
  );
}
